import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

/**
 * Auftragsverarbeitungsvertrag (AVV) Page
 * Data Processing Agreement per Art. 28 GDPR
 * Serves /avv
 */
const AVVPage = () => {
  const { i18n } = useTranslation()
  const isGerman = i18n.language === 'de'

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-600 hover:text-purple-600 transition-colors mb-4"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {isGerman ? 'Zurück' : 'Back'}
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            {isGerman ? 'Auftragsverarbeitungsvertrag (AVV)' : 'Data Processing Agreement (DPA)'}
          </h1>
          <p className="mt-2 text-gray-600">
            {isGerman
              ? 'gemäß Art. 28 Datenschutz-Grundverordnung (DSGVO)'
              : 'pursuant to Art. 28 General Data Protection Regulation (GDPR)'}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {isGerman ? 'Stand: Januar 2025' : 'As of: January 2025'}
          </p>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-8 space-y-8">

            {/* Vertragsparteien */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? 'Vertragsparteien' : 'Contracting Parties'}
              </h2>
              <div className="space-y-4 text-gray-700 text-sm">
                <div>
                  <p className="font-semibold">{isGerman ? 'Auftraggeber (Verantwortlicher):' : 'Controller (Data Controller):'}</p>
                  <p>
                    {isGerman
                      ? 'Der Nutzer der SaaS-Plattform &quot;Story Planner Pro&quot;, der sich durch Zustimmung zu den Allgemeinen Geschäftsbedingungen und diesem Auftragsverarbeitungsvertrag registriert hat.'
                      : 'The user of the SaaS platform &quot;Story Planner Pro&quot; who has registered by accepting the General Terms and Conditions and this Data Processing Agreement.'}
                  </p>
                  <p className="mt-1">
                    {isGerman
                      ? <>(nachfolgend &quot;<strong>Auftraggeber</strong>&quot; oder &quot;<strong>Verantwortlicher</strong>&quot;)</>
                      : <>(hereinafter referred to as the &quot;<strong>Controller</strong>&quot; or &quot;<strong>Data Controller</strong>&quot;)</>}
                  </p>
                </div>

                <hr className="border-gray-200" />

                <div>
                  <p className="font-semibold">{isGerman ? 'Auftragnehmer (Auftragsverarbeiter):' : 'Processor (Data Processor):'}</p>
                  <div className="ml-4 mt-2">
                    <p className="font-semibold">Emporion Innovations GmbH</p>
                    <p>Ludwigstr. 32</p>
                    <p>63067 Offenbach am Main, Deutschland</p>
                    <p className="mt-2">{isGerman ? 'Vertreten durch die Geschäftsführer:' : 'Represented by the Managing Directors:'}</p>
                    <p>Alexander Kuchta, Dennis Schmitz</p>
                    <p className="mt-2">
                      {isGerman ? 'Telefon' : 'Phone'}: +49 (0) 69 99998088<br/>
                      {isGerman ? 'E-Mail' : 'Email'}: <a href="mailto:info@emporion.technology" className="text-purple-600 hover:text-purple-700">info@emporion.technology</a>
                    </p>
                    <p className="mt-2">
                      {isGerman ? 'Registergericht: Amtsgericht Offenbach am Main' : 'Court of Registration: Amtsgericht Offenbach am Main'}<br/>
                      {isGerman ? 'Registernummer' : 'Registration Number'}: HRB 51283<br/>
                      {isGerman ? 'USt-IdNr.' : 'VAT ID'}: DE284997944
                    </p>
                  </div>
                  <p className="mt-2">
                    {isGerman
                      ? <>(nachfolgend &quot;<strong>Auftragnehmer</strong>&quot; oder &quot;<strong>Auftragsverarbeiter</strong>&quot;)</>
                      : <>(hereinafter referred to as the &quot;<strong>Processor</strong>&quot; or &quot;<strong>Data Processor</strong>&quot;)</>}
                  </p>
                </div>

                <p>
                  {isGerman
                    ? <>Auftraggeber und Auftragnehmer werden nachfolgend einzeln auch als &quot;<strong>Partei</strong>&quot; und gemeinsam als &quot;<strong>Parteien</strong>&quot; bezeichnet.</>
                    : <>The Controller and the Processor are hereinafter individually referred to as a &quot;<strong>Party</strong>&quot; and collectively as the &quot;<strong>Parties</strong>&quot;.</>}
                </p>
              </div>
            </section>

            {/* Präambel */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? 'Präambel' : 'Preamble'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? 'Der Auftragnehmer erbringt für den Auftraggeber Leistungen im Rahmen der SaaS-Plattform &quot;Story Planner Pro&quot;, insbesondere das Hosting von Link-in-Bio-Seiten. Im Rahmen dieser Leistungserbringung kann der Auftragnehmer in begrenztem Umfang personenbezogene Daten im Auftrag des Auftraggebers verarbeiten.'
                    : 'The Processor provides services to the Controller within the scope of the SaaS platform &quot;Story Planner Pro&quot;, in particular the hosting of link-in-bio pages. In the course of providing these services, the Processor may process personal data on behalf of the Controller to a limited extent.'}
                </p>
                <p>
                  {isGerman
                    ? 'Dieser Auftragsverarbeitungsvertrag konkretisiert die datenschutzrechtlichen Rechte und Pflichten der Parteien gemäß Art. 28 DSGVO.'
                    : 'This Data Processing Agreement specifies the data protection rights and obligations of the Parties pursuant to Art. 28 GDPR.'}
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3">
                  <p className="font-semibold text-blue-800">{isGerman ? 'Hinweis zur Datenminimierung:' : 'Note on Data Minimization:'}</p>
                  <p className="text-blue-700 mt-1">
                    {isGerman
                      ? 'Der Auftragnehmer hat seine Dienste datenschutzfreundlich gestaltet. Die vom Auftragnehmer erfassten Nutzungsstatistiken (Seitenaufrufe, Klickzahlen) sind anonymisiert und stellen keine personenbezogenen Daten dar. IP-Adressen von Besuchern der Link-in-Bio-Seiten werden nicht gespeichert. Der Umfang der Auftragsverarbeitung ist daher bewusst gering gehalten.'
                      : 'The Processor has designed its services in a privacy-friendly manner. The usage statistics collected by the Processor (page views, click counts) are anonymized and do not constitute personal data. IP addresses of visitors to the link-in-bio pages are not stored. The scope of data processing is therefore intentionally kept to a minimum.'}
                  </p>
                </div>
              </div>
            </section>

            {/* § 1 */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '§ 1 Gegenstand und Dauer der Verarbeitung' : '§ 1 Subject Matter and Duration of Processing'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? <><strong>(1)</strong> Gegenstand dieses Vertrages ist die Verarbeitung personenbezogener Daten durch den Auftragnehmer im Auftrag des Auftraggebers im Rahmen der Bereitstellung der SaaS-Plattform &quot;Story Planner Pro&quot;, insbesondere:</>
                    : <><strong>(1)</strong> The subject matter of this Agreement is the processing of personal data by the Processor on behalf of the Controller within the scope of providing the SaaS platform &quot;Story Planner Pro&quot;, in particular:</>}
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>{isGerman ? 'Hosting und Bereitstellung von Link-in-Bio-Seiten' : 'Hosting and provision of link-in-bio pages'}</li>
                  <li>{isGerman ? 'Speicherung und Anzeige von Produktdaten aus Google Shopping Feeds' : 'Storage and display of product data from Google Shopping feeds'}</li>
                  <li>{isGerman ? 'Bereitstellung der technischen Infrastruktur für Cookie-Consent-Management (sofern der Auftraggeber eigene Tracking-Tools einbindet)' : 'Provision of technical infrastructure for cookie consent management (insofar as the Controller integrates their own tracking tools)'}</li>
                </ul>

                <p>
                  {isGerman
                    ? <><strong>(2)</strong> Folgende Verarbeitungen stellen <strong>keine Auftragsverarbeitung</strong> dar und fallen nicht unter diesen Vertrag:</>
                    : <><strong>(2)</strong> The following activities do <strong>not constitute data processing</strong> on behalf of the Controller and are not covered by this Agreement:</>}
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>{isGerman ? 'Erfassung anonymisierter Nutzungsstatistiken (Klickzahlen, Seitenaufrufe) – diese enthalten keine personenbezogenen Daten' : 'Collection of anonymized usage statistics (click counts, page views) – these do not contain personal data'}</li>
                  <li>{isGerman ? 'Technische Übertragung von IP-Adressen zur Seitenauslieferung – diese werden nicht gespeichert' : 'Technical transmission of IP addresses for page delivery – these are not stored'}</li>
                  <li>{isGerman ? 'Datenverarbeitung durch vom Auftraggeber eingebundene Drittanbieter-Tools (Facebook Pixel, Google Analytics) – hierfür ist der Auftraggeber eigenständig verantwortlich' : 'Data processing by third-party tools integrated by the Controller (Facebook Pixel, Google Analytics) – the Controller is solely responsible for these'}</li>
                </ul>

                <p>
                  {isGerman
                    ? <><strong>(3)</strong> Die Verarbeitung beginnt mit der Registrierung des Auftraggebers bei Story Planner Pro und endet mit der vollständigen Löschung aller im Auftrag verarbeiteten personenbezogenen Daten nach Beendigung des Hauptvertrages.</>
                    : <><strong>(3)</strong> Processing commences upon the Controller's registration with Story Planner Pro and ends upon the complete deletion of all personal data processed on behalf of the Controller after termination of the main agreement.</>}
                </p>
                <p>
                  {isGerman
                    ? <><strong>(4)</strong> Die Dauer der Verarbeitung entspricht der Laufzeit des Hauptvertrages (AGB) zuzüglich etwaiger gesetzlicher Aufbewahrungsfristen.</>
                    : <><strong>(4)</strong> The duration of processing corresponds to the term of the main agreement (Terms and Conditions) plus any statutory retention periods.</>}
                </p>
              </div>
            </section>

            {/* § 2 */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '§ 2 Art und Zweck der Verarbeitung' : '§ 2 Nature and Purpose of Processing'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? <><strong>(1)</strong> Der Auftragnehmer verarbeitet personenbezogene Daten ausschließlich zur Erfüllung des Hauptvertrages, insbesondere zu folgenden Zwecken:</>
                    : <><strong>(1)</strong> The Processor shall process personal data exclusively for the performance of the main agreement, in particular for the following purposes:</>}
                </p>
                <ul className="list-none space-y-2 ml-4">
                  <li>
                    {isGerman
                      ? <><strong>a) Bereitstellung der Link-in-Bio-Seiten:</strong> Technisches Hosting und Auslieferung der vom Auftraggeber erstellten Landing Pages</>
                      : <><strong>a) Provision of link-in-bio pages:</strong> Technical hosting and delivery of the landing pages created by the Controller</>}
                  </li>
                  <li>
                    {isGerman
                      ? <><strong>b) Cookie-Consent-Management:</strong> Technische Bereitstellung einer Consent-Lösung zur Verwaltung von Einwilligungen der Seitenbesucher für vom Auftraggeber eingebundene Tracking-Tools</>
                      : <><strong>b) Cookie consent management:</strong> Technical provision of a consent solution for managing visitor consents for tracking tools integrated by the Controller</>}
                  </li>
                  <li>
                    {isGerman
                      ? <><strong>c) Technischer Betrieb:</strong> Gewährleistung der Verfügbarkeit, Sicherheit und Funktionsfähigkeit der Plattform</>
                      : <><strong>c) Technical operations:</strong> Ensuring the availability, security, and functionality of the platform</>}
                  </li>
                </ul>
                <p>
                  {isGerman
                    ? <><strong>(2)</strong> Eine Verarbeitung für eigene Zwecke des Auftragnehmers findet nicht statt.</>
                    : <><strong>(2)</strong> Processing for the Processor's own purposes shall not take place.</>}
                </p>
                <p>
                  {isGerman
                    ? <><strong>(3)</strong> Der Auftragnehmer setzt auf den Link-in-Bio-Seiten <strong>keine eigenen Tracking-Tools</strong> (wie Facebook Pixel oder Google Analytics) ein und erhebt dort keine personenbezogenen Daten zu eigenen Zwecken.</>
                    : <><strong>(3)</strong> The Processor does not deploy any <strong>proprietary tracking tools</strong> (such as Facebook Pixel or Google Analytics) on the link-in-bio pages and does not collect personal data there for its own purposes.</>}
                </p>
              </div>
            </section>

            {/* § 3 */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '§ 3 Art der personenbezogenen Daten' : '§ 3 Types of Personal Data'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? <><strong>(1)</strong> Gegenstand der Verarbeitung sind folgende Arten personenbezogener Daten:</>
                    : <><strong>(1)</strong> The following types of personal data are subject to processing:</>}
                </p>

                <p className="font-semibold mt-2">
                  {isGerman
                    ? 'a) Daten der Besucher von Link-in-Bio-Seiten (minimaler Umfang):'
                    : 'a) Data of visitors to link-in-bio pages (minimal scope):'}
                </p>
                <ul className="list-disc list-inside ml-4">
                  <li>{isGerman ? 'Cookie-Einwilligungsstatus und -präferenzen (sofern vom Auftraggeber Tracking-Tools eingebunden werden)' : 'Cookie consent status and preferences (insofar as tracking tools are integrated by the Controller)'}</li>
                </ul>
                <p className="mt-2">
                  {isGerman
                    ? <>Ausdrücklich <strong>nicht</strong> verarbeitet werden:</>
                    : <>Expressly <strong>not</strong> processed:</>}
                </p>
                <ul className="list-disc list-inside ml-4">
                  <li>{isGerman ? 'IP-Adressen (werden technisch übertragen, aber nicht gespeichert)' : 'IP addresses (technically transmitted but not stored)'}</li>
                  <li>{isGerman ? 'Tracking-Daten (werden nicht durch den Auftragnehmer erhoben)' : 'Tracking data (not collected by the Processor)'}</li>
                </ul>

                <p className="font-semibold mt-3">
                  {isGerman ? 'b) Daten des Auftraggebers:' : 'b) Data of the Controller:'}
                </p>
                <ul className="list-disc list-inside ml-4">
                  <li>{isGerman ? 'Kontaktdaten (Name, E-Mail-Adresse, Telefonnummer)' : 'Contact data (name, email address, phone number)'}</li>
                  <li>{isGerman ? 'Impressumsdaten (zur Anzeige auf Link-in-Bio-Seiten)' : 'Legal notice data (for display on link-in-bio pages)'}</li>
                  <li>{isGerman ? 'Zugangsdaten (verschlüsselt gespeichert)' : 'Access credentials (stored in encrypted form)'}</li>
                  <li>{isGerman ? 'Rechnungsdaten' : 'Billing data'}</li>
                </ul>

                <p className="mt-3">
                  {isGerman
                    ? <><strong>(2)</strong> Besondere Kategorien personenbezogener Daten im Sinne von Art. 9 Abs. 1 DSGVO werden nicht verarbeitet.</>
                    : <><strong>(2)</strong> Special categories of personal data within the meaning of Art. 9(1) GDPR shall not be processed.</>}
                </p>
              </div>
            </section>

            {/* § 4 */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '§ 4 Kategorien betroffener Personen' : '§ 4 Categories of Data Subjects'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>{isGerman ? 'Von der Verarbeitung sind folgende Kategorien betroffener Personen betroffen:' : 'The following categories of data subjects are affected by the processing:'}</p>
                <ul className="list-none space-y-2 ml-4">
                  <li>{isGerman ? <><strong>a)</strong> Der Auftraggeber selbst sowie dessen Mitarbeiter und Beauftragte</> : <><strong>a)</strong> The Controller themselves as well as their employees and authorized representatives</>}</li>
                  <li>{isGerman ? <><strong>b)</strong> Besucher der Link-in-Bio-Seiten – nur in dem begrenzten Umfang, in dem Cookie-Consent-Präferenzen gespeichert werden</> : <><strong>b)</strong> Visitors to the link-in-bio pages – only to the limited extent that cookie consent preferences are stored</>}</li>
                </ul>
              </div>
            </section>

            {/* § 5 */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '§ 5 Pflichten des Auftragnehmers' : '§ 5 Obligations of the Processor'}
              </h2>
              <div className="space-y-4 text-gray-700 text-sm">
                <div>
                  <p className="font-semibold">{isGerman ? '(1) Weisungsgebundenheit' : '(1) Obligation to Follow Instructions'}</p>
                  <p className="mt-1">
                    {isGerman
                      ? 'Der Auftragnehmer verarbeitet personenbezogene Daten ausschließlich auf dokumentierte Weisung des Auftraggebers, es sei denn, er ist durch das Recht der Union oder der Mitgliedstaaten zur Verarbeitung verpflichtet.'
                      : 'The Processor shall process personal data only on documented instructions from the Controller, unless required to do so by Union or Member State law.'}
                  </p>
                  <p className="mt-1">
                    {isGerman
                      ? <>Weisungen können in Textform (E-Mail) erteilt werden an: <a href="mailto:datenschutz@storyplannerpro.de" className="text-purple-600 hover:text-purple-700">datenschutz@storyplannerpro.de</a></>
                      : <>Instructions may be issued in text form (email) to: <a href="mailto:datenschutz@storyplannerpro.de" className="text-purple-600 hover:text-purple-700">datenschutz@storyplannerpro.de</a></>}
                  </p>
                  <p className="mt-1">
                    {isGerman
                      ? 'Der Auftragnehmer informiert den Auftraggeber unverzüglich, wenn er der Auffassung ist, dass eine Weisung gegen Datenschutzvorschriften verstößt.'
                      : 'The Processor shall immediately inform the Controller if, in its opinion, an instruction infringes data protection legislation.'}
                  </p>
                </div>

                <div>
                  <p className="font-semibold">{isGerman ? '(2) Vertraulichkeit' : '(2) Confidentiality'}</p>
                  <p className="mt-1">
                    {isGerman
                      ? 'Der Auftragnehmer gewährleistet, dass sich die zur Verarbeitung der personenbezogenen Daten befugten Personen zur Vertraulichkeit verpflichtet haben oder einer angemessenen gesetzlichen Verschwiegenheitspflicht unterliegen.'
                      : 'The Processor shall ensure that persons authorized to process personal data have committed themselves to confidentiality or are under an appropriate statutory obligation of confidentiality.'}
                  </p>
                </div>

                <div>
                  <p className="font-semibold">{isGerman ? '(3) Sicherheit der Verarbeitung' : '(3) Security of Processing'}</p>
                  <p className="mt-1">
                    {isGerman
                      ? <>Der Auftragnehmer ergreift alle gemäß Art. 32 DSGVO erforderlichen technischen und organisatorischen Maßnahmen. Die Maßnahmen sind in <strong>Anlage 1</strong> (TOMs) beschrieben.</>
                      : <>The Processor shall implement all technical and organizational measures required pursuant to Art. 32 GDPR. These measures are described in <strong>Annex 1</strong> (TOMs).</>}
                  </p>
                </div>

                <div>
                  <p className="font-semibold">{isGerman ? '(4) Sub-Auftragsverarbeiter' : '(4) Sub-processors'}</p>
                  <p className="mt-1">
                    {isGerman
                      ? <>Der Auftragnehmer nimmt die in <strong>Anlage 2</strong> genannten Sub-Auftragsverarbeiter in Anspruch. Der Auftraggeber erteilt hiermit seine allgemeine Genehmigung zur Hinzuziehung weiterer Sub-Auftragsverarbeiter.</>
                      : <>The Processor engages the sub-processors listed in <strong>Annex 2</strong>. The Controller hereby grants general authorization for the engagement of additional sub-processors.</>}
                  </p>
                  <p className="mt-1">
                    {isGerman
                      ? 'Der Auftragnehmer informiert den Auftraggeber über beabsichtigte Änderungen mindestens 14 Tage vor der geplanten Änderung per E-Mail. Der Auftraggeber kann gegen diese Änderung innerhalb von 14 Tagen Einspruch erheben.'
                      : 'The Processor shall inform the Controller of any intended changes at least 14 days prior to the planned change by email. The Controller may object to such changes within 14 days.'}
                  </p>
                  <p className="mt-1">
                    {isGerman
                      ? 'Der Auftragnehmer stellt sicher, dass den Sub-Auftragsverarbeitern dieselben Datenschutzpflichten auferlegt werden.'
                      : 'The Processor shall ensure that the same data protection obligations are imposed on sub-processors.'}
                  </p>
                </div>

                <div>
                  <p className="font-semibold">{isGerman ? '(5) Unterstützung des Auftraggebers' : '(5) Assistance to the Controller'}</p>
                  <p className="mt-1">{isGerman ? 'Der Auftragnehmer unterstützt den Auftraggeber unter Berücksichtigung der Art der Verarbeitung:' : 'The Processor shall assist the Controller, taking into account the nature of processing:'}</p>
                  <ul className="list-none ml-4 mt-1 space-y-1">
                    <li>{isGerman ? 'a) Bei der Erfüllung von Betroffenenanfragen gemäß Art. 15-22 DSGVO' : 'a) In fulfilling data subject requests pursuant to Art. 15-22 GDPR'}</li>
                    <li>{isGerman ? 'b) Bei der Einhaltung der Pflichten gemäß Art. 32-36 DSGVO' : 'b) In complying with obligations pursuant to Art. 32-36 GDPR'}</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold">{isGerman ? '(6) Löschung und Rückgabe' : '(6) Deletion and Return'}</p>
                  <p className="mt-1">
                    {isGerman
                      ? 'Nach Beendigung des Hauptvertrages löscht der Auftragnehmer alle personenbezogenen Daten innerhalb von 30 Tagen, sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen.'
                      : 'Upon termination of the main agreement, the Processor shall delete all personal data within 30 days, unless statutory retention obligations apply.'}
                  </p>
                </div>

                <div>
                  <p className="font-semibold">{isGerman ? '(7) Nachweispflichten' : '(7) Accountability Obligations'}</p>
                  <p className="mt-1">
                    {isGerman
                      ? 'Der Auftragnehmer stellt dem Auftraggeber alle erforderlichen Informationen zum Nachweis der Einhaltung der Pflichten nach Art. 28 DSGVO zur Verfügung und ermöglicht Überprüfungen.'
                      : 'The Processor shall make available to the Controller all information necessary to demonstrate compliance with the obligations laid down in Art. 28 GDPR and shall allow for and contribute to audits.'}
                  </p>
                  <p className="mt-1">
                    {isGerman
                      ? 'Überprüfungen vor Ort sind mit einer Ankündigungsfrist von mindestens 14 Tagen anzumelden.'
                      : 'On-site audits shall be notified with a minimum notice period of 14 days.'}
                  </p>
                </div>
              </div>
            </section>

            {/* § 6 */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '§ 6 Pflichten des Auftraggebers' : '§ 6 Obligations of the Controller'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? <><strong>(1)</strong> Der Auftraggeber ist für die Rechtmäßigkeit der Datenverarbeitung sowie für die Wahrung der Rechte der betroffenen Personen verantwortlich.</>
                    : <><strong>(1)</strong> The Controller shall be responsible for the lawfulness of data processing and for safeguarding the rights of data subjects.</>}
                </p>
                <p>
                  {isGerman
                    ? <><strong>(2)</strong> Der Auftraggeber ist insbesondere verantwortlich für:</>
                    : <><strong>(2)</strong> The Controller shall be particularly responsible for:</>}
                </p>
                <ul className="list-none ml-4 space-y-1">
                  <li>{isGerman ? 'a) Die Einholung wirksamer Einwilligungen für von ihm eingebundene Tracking-Tools (Facebook Pixel, Google Analytics)' : 'a) Obtaining valid consent for tracking tools integrated by the Controller (Facebook Pixel, Google Analytics)'}</li>
                  <li>{isGerman ? 'b) Die Bereitstellung einer vollständigen und rechtskonformen Datenschutzerklärung' : 'b) Providing a complete and legally compliant privacy policy'}</li>
                  <li>{isGerman ? 'c) Die Erfüllung von Betroffenenrechten gegenüber Besuchern seiner Link-in-Bio-Seiten' : 'c) Fulfilling data subject rights for visitors to their link-in-bio pages'}</li>
                  <li>{isGerman ? 'd) Den Abschluss erforderlicher Vereinbarungen mit Drittanbietern (Meta, Google)' : 'd) Entering into required agreements with third-party providers (Meta, Google)'}</li>
                </ul>
              </div>
            </section>

            {/* § 7 */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '§ 7 Meldung von Datenschutzverletzungen' : '§ 7 Notification of Personal Data Breaches'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? <><strong>(1)</strong> Der Auftragnehmer unterrichtet den Auftraggeber unverzüglich, nachdem ihm eine Verletzung des Schutzes personenbezogener Daten bekannt wurde.</>
                    : <><strong>(1)</strong> The Processor shall notify the Controller without undue delay after becoming aware of a personal data breach.</>}
                </p>
                <p>
                  {isGerman
                    ? <><strong>(2)</strong> Die Meldung enthält mindestens:</>
                    : <><strong>(2)</strong> The notification shall include at least:</>}
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>{isGerman ? 'Beschreibung der Art der Verletzung' : 'A description of the nature of the breach'}</li>
                  <li>{isGerman ? 'Kategorien und ungefähre Zahl der betroffenen Personen/Datensätze (soweit bekannt)' : 'The categories and approximate number of data subjects/records concerned (where known)'}</li>
                  <li>{isGerman ? 'Kontaktdaten für Rückfragen' : 'Contact details for further inquiries'}</li>
                  <li>{isGerman ? 'Beschreibung der wahrscheinlichen Folgen' : 'A description of the likely consequences'}</li>
                  <li>{isGerman ? 'Beschreibung der ergriffenen Maßnahmen' : 'A description of the measures taken or proposed'}</li>
                </ul>
                <p>
                  {isGerman
                    ? <><strong>(3)</strong> Der Auftragnehmer unterstützt den Auftraggeber bei der Erfüllung seiner Meldepflichten gemäß Art. 33, 34 DSGVO.</>
                    : <><strong>(3)</strong> The Processor shall assist the Controller in fulfilling its notification obligations pursuant to Art. 33, 34 GDPR.</>}
                </p>
              </div>
            </section>

            {/* § 8 */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '§ 8 Datenschutzbeauftragter' : '§ 8 Data Protection Officer'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>{isGerman ? 'Kontakt zum Datenschutzbeauftragten des Auftragnehmers:' : 'Contact for the Processor\'s Data Protection Officer:'}</p>
                <p className="ml-4">
                  {isGerman ? 'E-Mail' : 'Email'}: <a href="mailto:datenschutz@storyplannerpro.de" className="text-purple-600 hover:text-purple-700">datenschutz@storyplannerpro.de</a>
                </p>
              </div>
            </section>

            {/* § 9 */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '§ 9 Datenübermittlung in Drittländer' : '§ 9 Data Transfers to Third Countries'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? <><strong>(1)</strong> Eine Übermittlung personenbezogener Daten in ein Drittland erfolgt nur bei Vorliegen der Voraussetzungen der Art. 44-49 DSGVO.</>
                    : <><strong>(1)</strong> A transfer of personal data to a third country shall only take place where the conditions of Art. 44-49 GDPR are met.</>}
                </p>
                <p>
                  {isGerman
                    ? <><strong>(2)</strong> Der Auftragnehmer setzt Sub-Auftragsverarbeiter mit Sitz in den USA ein (siehe Anlage 2). Für diese Übermittlungen gelten folgende Garantien:</>
                    : <><strong>(2)</strong> The Processor engages sub-processors based in the USA (see Annex 2). The following safeguards apply to these transfers:</>}
                </p>
                <ul className="list-none ml-4 space-y-2">
                  <li>{isGerman ? <><strong>a) Microsoft Corporation (Azure):</strong> EU-US Data Privacy Framework (zertifiziert), Standardvertragsklauseln (SCCs), primäre Datenverarbeitung in EU-Rechenzentren</> : <><strong>a) Microsoft Corporation (Azure):</strong> EU-US Data Privacy Framework (certified), Standard Contractual Clauses (SCCs), primary data processing in EU data centers</>}</li>
                  <li>{isGerman ? <><strong>b) Supabase Inc.:</strong> EU-US Data Privacy Framework (zertifiziert), Standardvertragsklauseln (SCCs), Datenbank in EU-Region (Frankfurt)</> : <><strong>b) Supabase Inc.:</strong> EU-US Data Privacy Framework (certified), Standard Contractual Clauses (SCCs), database in EU region (Frankfurt)</>}</li>
                </ul>
                <p>
                  {isGerman
                    ? <><strong>(3)</strong> Bei Supabase werden ausschließlich anonymisierte Nutzungsstatistiken gespeichert, die keine personenbezogenen Daten darstellen.</>
                    : <><strong>(3)</strong> Only anonymized usage statistics are stored with Supabase, which do not constitute personal data.</>}
                </p>
              </div>
            </section>

            {/* § 10 */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '§ 10 Haftung' : '§ 10 Liability'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>
                  {isGerman
                    ? <><strong>(1)</strong> Für die Haftung gelten die Regelungen des Hauptvertrages (AGB), soweit nicht zwingende gesetzliche Vorschriften entgegenstehen.</>
                    : <><strong>(1)</strong> The liability provisions of the main agreement (Terms and Conditions) shall apply, unless mandatory statutory provisions provide otherwise.</>}
                </p>
                <p>
                  {isGerman
                    ? <><strong>(2)</strong> Die Parteien haften gegenüber betroffenen Personen gesamtschuldnerisch gemäß Art. 82 Abs. 4 DSGVO. Im Innenverhältnis haftet jede Partei für den von ihr zu vertretenden Teil des Schadens.</>
                    : <><strong>(2)</strong> The Parties shall be jointly and severally liable to data subjects pursuant to Art. 82(4) GDPR. In their internal relationship, each Party shall be liable for its share of the damage.</>}
                </p>
              </div>
            </section>

            {/* § 11 */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '§ 11 Laufzeit und Beendigung' : '§ 11 Term and Termination'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>{isGerman ? <><strong>(1)</strong> Dieser Vertrag ist an die Laufzeit des Hauptvertrages (AGB) gekoppelt.</> : <><strong>(1)</strong> This Agreement is linked to the term of the main agreement (Terms and Conditions).</>}</p>
                <p>{isGerman ? <><strong>(2)</strong> Eine Kündigung des Hauptvertrages beendet automatisch auch diesen Vertrag.</> : <><strong>(2)</strong> Termination of the main agreement shall automatically terminate this Agreement.</>}</p>
                <p>{isGerman ? <><strong>(3)</strong> Die Pflichten aus § 5 Abs. 6 (Löschung) und § 5 Abs. 7 (Nachweispflichten) gelten auch nach Beendigung fort.</> : <><strong>(3)</strong> The obligations under § 5(6) (Deletion) and § 5(7) (Accountability Obligations) shall survive termination.</>}</p>
              </div>
            </section>

            {/* § 12 */}
            <section className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {isGerman ? '§ 12 Schlussbestimmungen' : '§ 12 Final Provisions'}
              </h2>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>{isGerman ? <><strong>(1)</strong> Änderungen dieses Vertrages bedürfen der Textform.</> : <><strong>(1)</strong> Amendments to this Agreement shall be made in written or text form.</>}</p>
                <p>{isGerman ? <><strong>(2)</strong> Es gilt das Recht der Bundesrepublik Deutschland.</> : <><strong>(2)</strong> This Agreement shall be governed by the laws of the Federal Republic of Germany.</>}</p>
                <p>{isGerman ? <><strong>(3)</strong> Gerichtsstand ist, soweit gesetzlich zulässig, Offenbach am Main.</> : <><strong>(3)</strong> The place of jurisdiction shall be Offenbach am Main, to the extent permitted by law.</>}</p>
                <p>{isGerman ? <><strong>(4)</strong> Dieser Vertrag ist Bestandteil der AGB von Story Planner Pro. Bei Widersprüchen gehen die Regelungen dieses Vertrages vor, soweit sie den Datenschutz betreffen.</> : <><strong>(4)</strong> This Agreement forms an integral part of the Terms and Conditions of Story Planner Pro. In the event of any conflict, the provisions of this Agreement shall prevail insofar as they relate to data protection.</>}</p>
              </div>
            </section>

            {/* Anlage 1: TOMs */}
            <section className="border-t-4 border-gray-300 pt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {isGerman ? 'Anlage 1: Technische und organisatorische Maßnahmen (TOMs)' : 'Annex 1: Technical and Organizational Measures (TOMs)'}
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                {isGerman ? 'gemäß Art. 32 DSGVO — Stand: Januar 2025' : 'pursuant to Art. 32 GDPR — As of: January 2025'}
              </p>

              <div className="space-y-6 text-gray-700 text-sm">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="font-semibold text-blue-800">{isGerman ? 'Vorbemerkung' : 'Preliminary Note'}</p>
                  <p className="text-blue-700 mt-1">
                    {isGerman
                      ? <>Der Auftragnehmer hat seine Dienste nach dem Prinzip der <strong>Datenminimierung</strong> gestaltet:</>
                      : <>The Processor has designed its services in accordance with the principle of <strong>data minimization</strong>:</>}
                  </p>
                  <ul className="list-disc list-inside ml-2 mt-1 text-blue-700 space-y-1">
                    <li>{isGerman ? <><strong>IP-Adressen</strong> von Besuchern werden nicht gespeichert</> : <><strong>IP addresses</strong> of visitors are not stored</>}</li>
                    <li>{isGerman ? <><strong>Nutzungsstatistiken</strong> (Klickzahlen) werden anonymisiert erfasst und stellen keine personenbezogenen Daten dar</> : <><strong>Usage statistics</strong> (click counts) are collected in anonymized form and do not constitute personal data</>}</li>
                    <li>{isGerman ? <><strong>Tracking-Tools</strong> werden vom Auftragnehmer nicht auf Nutzer-Seiten eingesetzt</> : <><strong>Tracking tools</strong> are not deployed by the Processor on user pages</>}</li>
                  </ul>
                </div>

                {/* 1. Vertraulichkeit */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {isGerman ? '1. Vertraulichkeit (Art. 32 Abs. 1 lit. b DSGVO)' : '1. Confidentiality (Art. 32(1)(b) GDPR)'}
                  </h3>

                  <p className="font-semibold mt-3">{isGerman ? '1.1 Zutrittskontrolle' : '1.1 Physical Access Control'}</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>{isGerman ? 'Datenverarbeitung erfolgt ausschließlich in professionellen Rechenzentren (Microsoft Azure)' : 'Data processing takes place exclusively in professional data centers (Microsoft Azure)'}</li>
                    <li>{isGerman ? 'Rechenzentren mit 24/7 Sicherheitspersonal, biometrischen Zugangskontrollen, Videoüberwachung' : 'Data centers with 24/7 security personnel, biometric access controls, video surveillance'}</li>
                    <li>{isGerman ? 'Der Auftragnehmer betreibt keine eigenen Server mit personenbezogenen Daten' : 'The Processor does not operate its own servers containing personal data'}</li>
                  </ul>

                  <p className="font-semibold mt-3">{isGerman ? '1.2 Zugangskontrolle' : '1.2 System Access Control'}</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>{isGerman ? 'Authentifizierung durch individuelle Benutzerkonten' : 'Authentication through individual user accounts'}</li>
                    <li>{isGerman ? 'Zwei-Faktor-Authentifizierung (2FA) für administrative Zugänge' : 'Two-factor authentication (2FA) for administrative access'}</li>
                    <li>{isGerman ? 'Automatische Sperrung nach fehlgeschlagenen Anmeldeversuchen' : 'Automatic lockout after failed login attempts'}</li>
                    <li>{isGerman ? 'Verschlüsselte Übertragung (TLS 1.3)' : 'Encrypted transmission (TLS 1.3)'}</li>
                    <li>{isGerman ? 'Session-Timeout bei Inaktivität' : 'Session timeout on inactivity'}</li>
                  </ul>

                  <p className="font-semibold mt-3">{isGerman ? '1.3 Zugriffskontrolle' : '1.3 Data Access Control'}</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>{isGerman ? 'Rollenbasiertes Berechtigungskonzept' : 'Role-based access control concept'}</li>
                    <li>{isGerman ? 'Prinzip der minimalen Berechtigung' : 'Principle of least privilege'}</li>
                    <li>{isGerman ? 'Mandantentrennung: Jeder Auftraggeber kann nur auf seine eigenen Daten zugreifen' : 'Tenant isolation: Each Controller can only access their own data'}</li>
                    <li>{isGerman ? 'Protokollierung von Datenzugriffen' : 'Logging of data access'}</li>
                  </ul>

                  <p className="font-semibold mt-3">{isGerman ? '1.4 Trennungskontrolle' : '1.4 Separation Control'}</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>{isGerman ? 'Logische Mandantentrennung' : 'Logical tenant separation'}</li>
                    <li>{isGerman ? 'Trennung von Produktiv- und Testumgebungen' : 'Separation of production and test environments'}</li>
                  </ul>

                  <p className="font-semibold mt-3">{isGerman ? '1.5 Pseudonymisierung und Verschlüsselung' : '1.5 Pseudonymization and Encryption'}</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>{isGerman ? <><strong>IP-Adressen werden nicht gespeichert</strong> (Privacy by Design)</> : <><strong>IP addresses are not stored</strong> (Privacy by Design)</>}</li>
                    <li>{isGerman ? 'Nutzungsstatistiken sind anonymisiert' : 'Usage statistics are anonymized'}</li>
                    <li>{isGerman ? 'Verschlüsselung ruhender Daten (AES-256)' : 'Encryption of data at rest (AES-256)'}</li>
                    <li>{isGerman ? 'Verschlüsselung bei Übertragung (TLS 1.3)' : 'Encryption in transit (TLS 1.3)'}</li>
                    <li>{isGerman ? 'Passwörter als Hashwerte (bcrypt)' : 'Passwords stored as hashes (bcrypt)'}</li>
                  </ul>
                </div>

                {/* 2. Integrität */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {isGerman ? '2. Integrität (Art. 32 Abs. 1 lit. b DSGVO)' : '2. Integrity (Art. 32(1)(b) GDPR)'}
                  </h3>

                  <p className="font-semibold">{isGerman ? '2.1 Weitergabekontrolle' : '2.1 Transfer Control'}</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>{isGerman ? 'Verschlüsselung aller Datenübertragungen (TLS 1.3, HTTPS)' : 'Encryption of all data transmissions (TLS 1.3, HTTPS)'}</li>
                    <li>{isGerman ? 'Sichere API-Authentifizierung' : 'Secure API authentication'}</li>
                    <li>{isGerman ? 'Keine Übertragung sensibler Daten per unverschlüsselter E-Mail' : 'No transmission of sensitive data via unencrypted email'}</li>
                  </ul>

                  <p className="font-semibold mt-3">{isGerman ? '2.2 Eingabekontrolle' : '2.2 Input Control'}</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>{isGerman ? 'Protokollierung von Dateneingaben, -änderungen und -löschungen' : 'Logging of data entries, modifications, and deletions'}</li>
                    <li>{isGerman ? 'Audit-Logs mit Zeitstempel und Benutzerkennung' : 'Audit logs with timestamps and user identifiers'}</li>
                  </ul>
                </div>

                {/* 3. Verfügbarkeit */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {isGerman ? '3. Verfügbarkeit und Belastbarkeit (Art. 32 Abs. 1 lit. b, c DSGVO)' : '3. Availability and Resilience (Art. 32(1)(b), (c) GDPR)'}
                  </h3>

                  <p className="font-semibold">{isGerman ? '3.1 Verfügbarkeitskontrolle' : '3.1 Availability Control'}</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>{isGerman ? 'Hosting in georedundanten Rechenzentren (Microsoft Azure)' : 'Hosting in geo-redundant data centers (Microsoft Azure)'}</li>
                    <li>{isGerman ? 'Automatische tägliche Backups' : 'Automatic daily backups'}</li>
                    <li>{isGerman ? 'Aufbewahrung von Backups für mindestens 30 Tage' : 'Retention of backups for a minimum of 30 days'}</li>
                    <li>{isGerman ? 'DDoS-Schutz' : 'DDoS protection'}</li>
                  </ul>

                  <p className="font-semibold mt-3">{isGerman ? '3.2 Wiederherstellbarkeit' : '3.2 Recoverability'}</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>{isGerman ? 'Dokumentierte Disaster-Recovery-Prozesse' : 'Documented disaster recovery processes'}</li>
                    <li>{isGerman ? 'Recovery Time Objective (RTO): 24 Stunden' : 'Recovery Time Objective (RTO): 24 hours'}</li>
                    <li>{isGerman ? 'Recovery Point Objective (RPO): 24 Stunden' : 'Recovery Point Objective (RPO): 24 hours'}</li>
                  </ul>
                </div>

                {/* 4. Verfahren */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {isGerman ? '4. Verfahren zur Überprüfung (Art. 32 Abs. 1 lit. d DSGVO)' : '4. Procedures for Regular Review (Art. 32(1)(d) GDPR)'}
                  </h3>

                  <p className="font-semibold">{isGerman ? '4.1 Datenschutz-Management' : '4.1 Data Protection Management'}</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>{isGerman ? 'Dokumentiertes Datenschutz-Management-System' : 'Documented data protection management system'}</li>
                    <li>{isGerman ? 'Regelmäßige Überprüfung der TOMs' : 'Regular review of TOMs'}</li>
                    <li>{isGerman ? 'Verzeichnis von Verarbeitungstätigkeiten' : 'Records of processing activities'}</li>
                  </ul>

                  <p className="font-semibold mt-3">{isGerman ? '4.2 Privacy by Design / Privacy by Default' : '4.2 Privacy by Design / Privacy by Default'}</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>{isGerman ? <><strong>Datenminimierung:</strong> Keine Speicherung von IP-Adressen</> : <><strong>Data minimization:</strong> No storage of IP addresses</>}</li>
                    <li>{isGerman ? <><strong>Anonymisierung:</strong> Nutzungsstatistiken ohne Personenbezug</> : <><strong>Anonymization:</strong> Usage statistics without personal reference</>}</li>
                    <li>{isGerman ? <><strong>Kein eigenes Tracking:</strong> Keine FB Pixel/GA4 auf Nutzer-Seiten</> : <><strong>No proprietary tracking:</strong> No FB Pixel/GA4 on user pages</>}</li>
                    <li>{isGerman ? 'Voreingestellte datenschutzfreundliche Optionen' : 'Privacy-friendly default settings'}</li>
                  </ul>

                  <p className="font-semibold mt-3">{isGerman ? '4.3 Auftragskontrolle' : '4.3 Order Control'}</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>{isGerman ? 'Sorgfältige Auswahl von Sub-Auftragsverarbeitern' : 'Careful selection of sub-processors'}</li>
                    <li>{isGerman ? 'Auftragsverarbeitungsverträge mit allen Sub-Auftragsverarbeitern' : 'Data processing agreements with all sub-processors'}</li>
                  </ul>

                  <p className="font-semibold mt-3">{isGerman ? '4.4 Schulung' : '4.4 Training'}</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 mt-1">
                    <li>{isGerman ? 'Regelmäßige Datenschutzschulungen für Mitarbeiter' : 'Regular data protection training for employees'}</li>
                    <li>{isGerman ? 'Verpflichtung auf Vertraulichkeit' : 'Commitment to confidentiality'}</li>
                  </ul>
                </div>

                {/* Zertifizierungen */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {isGerman ? 'Zertifizierungen der Sub-Auftragsverarbeiter' : 'Certifications of Sub-processors'}
                  </h3>
                  <div className="space-y-2">
                    <p><strong>Microsoft Azure:</strong> ISO 27001, ISO 27017, ISO 27018, SOC 1, SOC 2, SOC 3, EU-US Data Privacy Framework</p>
                    <p><strong>Supabase:</strong> SOC 2 Type II, EU-US Data Privacy Framework, {isGerman ? 'Hosting in EU (Frankfurt)' : 'Hosting in EU (Frankfurt)'}</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Anlage 2: Sub-Auftragsverarbeiter */}
            <section className="border-t-4 border-gray-300 pt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {isGerman ? 'Anlage 2: Liste der Sub-Auftragsverarbeiter' : 'Annex 2: List of Sub-processors'}
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                {isGerman ? 'gemäß Art. 28 Abs. 2, 4 DSGVO — Stand: Januar 2025' : 'pursuant to Art. 28(2), (4) GDPR — As of: January 2025'}
              </p>

              <div className="space-y-6 text-gray-700 text-sm">
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-300 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{isGerman ? 'Nr.' : 'No.'}</th>
                        <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{isGerman ? 'Unternehmen' : 'Company'}</th>
                        <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{isGerman ? 'Verarbeitungstätigkeit' : 'Processing Activity'}</th>
                        <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{isGerman ? 'Verarbeitungsort' : 'Processing Location'}</th>
                        <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{isGerman ? 'Garantien' : 'Safeguards'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-gray-300 px-3 py-2">1</td>
                        <td className="border border-gray-300 px-3 py-2 font-semibold">Microsoft Corporation (Azure)</td>
                        <td className="border border-gray-300 px-3 py-2">{isGerman ? 'Hosting (Azure Static Web Apps, Azure Functions)' : 'Hosting (Azure Static Web Apps, Azure Functions)'}</td>
                        <td className="border border-gray-300 px-3 py-2">EU (West Europe, Germany West Central)</td>
                        <td className="border border-gray-300 px-3 py-2">{isGerman ? 'EU-US DPF, SCCs, EU-Rechenzentren' : 'EU-US DPF, SCCs, EU data centers'}</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 px-3 py-2">2</td>
                        <td className="border border-gray-300 px-3 py-2 font-semibold">Supabase Inc.</td>
                        <td className="border border-gray-300 px-3 py-2">{isGerman ? 'Datenbankhosting' : 'Database hosting'}</td>
                        <td className="border border-gray-300 px-3 py-2">EU (Frankfurt, aws eu-central-1)</td>
                        <td className="border border-gray-300 px-3 py-2">{isGerman ? 'EU-US DPF, SCCs, EU-Rechenzentren' : 'EU-US DPF, SCCs, EU data centers'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {isGerman ? 'Erläuterungen' : 'Explanatory Notes'}
                  </h3>

                  <p className="font-semibold mt-3">Microsoft Azure</p>
                  <p className="mt-1">
                    {isGerman
                      ? 'Wird eingesetzt für Azure Static Web Apps (Hosting der Link-in-Bio-Seiten) und Azure Functions (Backend-Services). Primäre Datenverarbeitung in EU-Rechenzentren. Microsoft ist unter dem EU-US Data Privacy Framework zertifiziert. Zusätzlich sind Standardvertragsklauseln vereinbart.'
                      : 'Used for Azure Static Web Apps (hosting of link-in-bio pages) and Azure Functions (backend services). Primary data processing in EU data centers. Microsoft is certified under the EU-US Data Privacy Framework. Additionally, Standard Contractual Clauses have been agreed upon.'}
                  </p>

                  <p className="font-semibold mt-3">Supabase</p>
                  <p className="mt-1">
                    {isGerman
                      ? 'Wird eingesetzt für die Speicherung von Nutzer-Account-Daten, anonymisierten Klickstatistiken (keine personenbezogenen Daten) und Konfigurationsdaten. Die Datenbank ist in der EU-Region (Frankfurt) konfiguriert. Supabase ist unter dem EU-US Data Privacy Framework zertifiziert.'
                      : 'Used for storage of user account data, anonymized click statistics (no personal data), and configuration data. The database is configured in the EU region (Frankfurt). Supabase is certified under the EU-US Data Privacy Framework.'}
                  </p>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-2">
                    <p className="text-yellow-800 text-sm">
                      {isGerman
                        ? <><strong>Wichtiger Hinweis:</strong> Die in Supabase gespeicherten Nutzungsstatistiken (Klickzahlen, Seitenaufrufe) sind anonymisiert und enthalten keine personenbezogenen Daten wie IP-Adressen. Diese Verarbeitung fällt daher nicht unter den Anwendungsbereich der DSGVO.</>
                        : <><strong>Important Note:</strong> The usage statistics stored in Supabase (click counts, page views) are anonymized and do not contain personal data such as IP addresses. This processing therefore falls outside the scope of the GDPR.</>}
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {isGerman ? 'Hinweis zu Drittanbieter-Tools des Auftraggebers' : 'Note on Third-Party Tools of the Controller'}
                  </h3>
                  <p>
                    {isGerman
                      ? <>Die folgenden Tools können vom Auftraggeber <strong>optional</strong> auf seinen Link-in-Bio-Seiten eingebunden werden. Diese stellen <strong>keine Sub-Auftragsverarbeiter des Auftragnehmers</strong> dar:</>
                      : <>The following tools may be <strong>optionally</strong> integrated by the Controller on their link-in-bio pages. These do <strong>not constitute sub-processors of the Processor</strong>:</>}
                  </p>
                  <div className="overflow-x-auto mt-2">
                    <table className="min-w-full border border-gray-300 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{isGerman ? 'Anbieter' : 'Provider'}</th>
                          <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{isGerman ? 'Tool' : 'Tool'}</th>
                          <th className="border border-gray-300 px-3 py-2 text-left font-semibold">{isGerman ? 'Verantwortlichkeit' : 'Responsibility'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-gray-300 px-3 py-2 font-semibold">Meta Platforms Ireland Ltd.</td>
                          <td className="border border-gray-300 px-3 py-2">Facebook Pixel</td>
                          <td className="border border-gray-300 px-3 py-2">{isGerman ? 'Gemeinsame Verantwortlichkeit Auftraggeber/Meta' : 'Joint controllership Controller/Meta'}</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 px-3 py-2 font-semibold">Google Ireland Ltd.</td>
                          <td className="border border-gray-300 px-3 py-2">Google Analytics 4</td>
                          <td className="border border-gray-300 px-3 py-2">{isGerman ? 'Auftraggeber als Verantwortlicher' : 'Controller as data controller'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-2 font-semibold">{isGerman ? 'Der Auftragnehmer selbst setzt diese Tools auf den Link-in-Bio-Seiten nicht ein.' : 'The Processor itself does not deploy these tools on the link-in-bio pages.'}</p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {isGerman ? 'Änderungen' : 'Changes'}
                  </h3>
                  <p>
                    {isGerman
                      ? 'Änderungen an dieser Liste werden dem Auftraggeber gemäß § 5 Abs. 4 des AVV mindestens 14 Tage vor Wirksamwerden per E-Mail mitgeteilt.'
                      : 'Changes to this list shall be communicated to the Controller in accordance with § 5(4) of the DPA at least 14 days prior to taking effect by email.'}
                  </p>
                  <p className="mt-1 text-gray-500">{isGerman ? 'Letzte Aktualisierung: Januar 2025' : 'Last updated: January 2025'}</p>
                </div>
              </div>
            </section>

            {/* Schluss */}
            <section className="border-t border-gray-200 pt-6">
              <div className="text-gray-700 text-sm space-y-2">
                <p className="font-semibold">Emporion Innovations GmbH</p>
                <p>Ludwigstr. 32<br/>63067 Offenbach am Main, Deutschland</p>
                <p>
                  {isGerman ? 'E-Mail' : 'Email'}: <a href="mailto:datenschutz@storyplannerpro.de" className="text-purple-600 hover:text-purple-700">datenschutz@storyplannerpro.de</a><br/>
                  Web: https://storyplannerpro.de
                </p>
                <p className="mt-4 text-xs text-gray-500 italic">
                  {isGerman
                    ? 'Dieser Auftragsverarbeitungsvertrag tritt mit Zustimmung zu den Allgemeinen Geschäftsbedingungen von Story Planner Pro in Kraft.'
                    : 'This Data Processing Agreement takes effect upon acceptance of the Terms and Conditions of Story Planner Pro.'}
                </p>
              </div>
            </section>

          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center">
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600">
            <Link to={isGerman ? '/impressum' : '/imprint'} className="hover:text-purple-600">
              {isGerman ? 'Impressum' : 'Imprint'}
            </Link>
            <Link to={isGerman ? '/datenschutz' : '/privacy'} className="hover:text-purple-600">
              {isGerman ? 'Datenschutz' : 'Privacy Policy'}
            </Link>
            <Link to="/terms" className="hover:text-purple-600">
              {isGerman ? 'AGB' : 'Terms'}
            </Link>
            <Link to="/contact" className="hover:text-purple-600">
              {isGerman ? 'Kontakt' : 'Contact'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AVVPage
